import React from 'react'
import { TreeNodeLike } from '../../utils/datasetTree'
import { formatShortDatetime } from '../../utils/date'

/**
 * 데이터셋 버전 계보를 세로 타임라인으로 그린다.
 *
 * 두 곳에서 같은 UI 를 쓴다:
 *   - 작업 화면(2열)의 계열 목록      layout="row"     (날짜를 이름 오른쪽에)
 *   - 데이터셋 상세(3열) 버전 히스토리  layout="stacked" (날짜를 이름 아래에)
 *
 * 예전에는 두 곳에 따로 구현해서 점 색이 서로 달라지는(회색 vs 파랑) 불일치가 있었다.
 * 표시 규칙은 여기서만 정한다: **최신 = 초록, 이전 버전 = 파랑**.
 *
 * 노드 높이는 설명 유무와 무관하게 같다 — 설명 칸을 항상 자리잡아 두고 한 줄로
 * 잘라낸다. 예전에는 설명이 없는 버전만 높이가 줄어 점 간격이 들쭉날쭉했고,
 * 설명이 길면 그 노드만 늘어났다. 전체 설명은 마우스를 올리면 보인다.
 *
 * 시각은 반드시 formatShortDatetime 을 쓴다. 서버가 타임존 표시 없는 UTC
 * (2026-08-03T06:46:13) 를 주므로 new Date() 에 그대로 넣으면 브라우저가
 * 로컬시간으로 해석해 9시간 어긋난다.
 */
export interface VersionTimelineProps<T extends TreeNodeLike> {
  versions: T[]
  /** 계보상 최신 버전 id — 초록 점 + 「최신」 배지 */
  currentId?: number | null
  /** 현재 선택된 버전 id — 이름에 밑줄 (stacked 에서만 의미 있음) */
  selectedId?: number | null
  layout?: 'row' | 'stacked'
  onSelect: (v: T) => void
}

/**
 * 점의 위쪽 여백 = 이름 줄 텍스트 중심까지의 거리(px). 점 중심을 이름과 맞춘다.
 *
 *   row     : 버튼 py-1.5(6) + 이름줄 h-5 의 절반(10) = 16 → 점 위 여백 16-5 = 11
 *   stacked : 이름줄 h-5 의 절반(10)                      → 점 위 여백 10-5 = 5
 *
 * 이름 줄 높이(h-5)나 버튼 패딩을 바꾸면 이 값도 같이 맞춰야 한다.
 */
const DOT_TOP: Record<'row' | 'stacked', number> = { row: 11, stacked: 5 }

export default function VersionTimeline<T extends TreeNodeLike>({
  versions,
  currentId = null,
  selectedId = null,
  layout = 'row',
  onSelect,
}: VersionTimelineProps<T>) {
  if (versions.length === 0) return null

  return (
    <div className="space-y-0">
      {versions.map((v, i) => {
        const isCurrent = currentId != null ? currentId === v.id : i === 0
        const isSelected = selectedId === v.id
        const isLast = i === versions.length - 1
        const when = v.created_at ? formatShortDatetime(v.created_at) : ''
        const desc = (v.description || '').trim()

        return (
          <div key={v.id} className="flex gap-3">
            {/*
              계보 라인 — 최신=초록, 이전 버전=파랑

              선을 점 아래쪽에만 그리면 행 경계마다 DOT_TOP 만큼 끊겨 보인다.
              점 위쪽에도 같은 높이의 선을 깔아 행을 넘어 이어지게 한다
              (첫 항목은 자리만 차지하고 선은 그리지 않는다).
            */}
            <div className="flex flex-col items-center">
              <span
                className={`w-px flex-shrink-0 ${i === 0 ? '' : 'bg-gray-200'}`}
                style={{ height: DOT_TOP[layout] }}
              />
              <span
                className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                  isCurrent ? 'bg-green-500' : 'bg-blue-400'
                }`}
              />
              {!isLast && <span className="flex-1 w-px bg-gray-200" />}
            </div>

            {layout === 'row' ? (
              <button
                className="flex-1 min-w-0 text-left py-1.5 px-2 -mx-2 rounded hover:bg-gray-50"
                onClick={() => onSelect(v)}
              >
                {/* 이름이 길어도 줄바꿈하지 않는다 — 줄이 늘면 노드 높이가 달라진다 */}
                <div className="flex items-center gap-2 h-5">
                  <span className="text-sm font-medium text-gray-900 truncate min-w-0" title={v.name}>
                    {v.name}
                  </span>
                  {isCurrent && <LatestBadge />}
                  <span className="text-xs text-gray-400 ml-auto flex-shrink-0">{when}</span>
                </div>
                <Description text={desc} />
              </button>
            ) : (
              <div className="flex-1 min-w-0 pb-3">
                <div className="flex items-center gap-2 h-5">
                  <button
                    className={`text-sm font-medium text-gray-900 hover:underline truncate min-w-0 ${isSelected ? 'underline' : ''}`}
                    onClick={() => onSelect(v)}
                    title={v.name}
                  >
                    {v.name}
                  </button>
                  {isCurrent && <LatestBadge />}
                </div>
                <div className="text-xs text-gray-500 mt-0.5 h-4 leading-4">{when}</div>
                <Description text={desc} />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

/**
 * 설명 한 줄. 내용이 없어도 자리를 차지해 노드 높이를 일정하게 유지한다.
 *
 * h-4 leading-4 = text-xs 한 줄 높이(16px)로 고정하고 truncate 로 잘라낸다.
 * 여러 줄 허용(line-clamp)으로 두면 설명 길이에 따라 노드마다 높이가 달라져
 * 계보 점 간격이 어긋난다. 전체 내용은 title 로 확인한다.
 */
function Description({ text }: { text: string }) {
  return (
    <div
      className="text-xs text-gray-500 mt-0.5 h-4 leading-4 truncate"
      title={text || undefined}
    >
      {text}
    </div>
  )
}

function LatestBadge() {
  return (
    <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-800 text-[10px] font-medium">
      최신
    </span>
  )
}
